import { useMemo } from "react";
import styled from "styled-components";
import { useFormikContext } from "formik";
import { Box } from "../../components/Box";
import { Flex } from "../../components/Flex";
import { Paragraph } from "../../components/Paragraph";
import { Toggle } from "../../components/Toggle";
import { useDivisions } from "../../data/useDivisions";

type DivisionValues = { divisions: number[] };

/** Same compact toggle the sharing panel and top-bar selector use. */
const SmallToggle = (props: React.ComponentProps<typeof Toggle>) => (
  <Box style={{ transform: "scale(0.7)", transformOrigin: "right center" }}>
    <Toggle {...props} />
  </Box>
);

/**
 * One Division per row, capped at ~480px so rows don't stretch across the
 * form; scrolls after about six.
 */
const TileList = styled(Box)`
  display: flex;
  flex-direction: column;
  max-width: 480px;
  max-height: 320px;
  overflow-y: auto;
  ${({ theme: { space } }) => `gap: ${space[1]};`}
`;

/** The whole tile is clickable, not just its toggle. */
const Tile = styled(Flex)<{
  isSelected: boolean;
  hasError: boolean;
  isDisabled: boolean;
}>`
  ${({ isSelected, hasError, isDisabled, theme: { colors, radii, space } }) => `
    justify-content: space-between;
    align-items: center;
    gap: ${space[1]};
    min-height: 36px;
    flex-shrink: 0;
    box-sizing: border-box;
    padding: 0 ${space[1]} 0 ${space[2]};
    border-radius: ${radii[0]};
    border: 1px solid ${
      hasError
        ? colors.red.primary
        : isSelected
        ? colors.blue.primary
        : colors.gray[20]
    };
    background: ${isSelected ? colors.blue[50] : colors.white};
    cursor: ${isDisabled ? "not-allowed" : "pointer"};
    opacity: ${isDisabled ? 0.6 : 1};
    transition: border-color 0.1s ease, background 0.1s ease;

    &:hover {
      border-color: ${
        hasError || isDisabled ? "" : colors.blue.primary
      };
    }
  `}
`;

const TileName = styled(Paragraph)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DefaultPill = styled.span`
  ${({ theme: { colors, radii, fontSizes } }) => `
    flex-shrink: 0;
    padding: 1px 6px;
    border-radius: ${radii[0]};
    background: ${colors.gray[10]};
    color: ${colors.gray[60]};
    font-family: "Inter", sans-serif;
    font-size: ${fontSizes[0]};
    white-space: nowrap;
  `}
`;

const TextButton = styled.button`
  ${({ theme: { colors, fontSizes } }) => `
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    font-family: "Inter", sans-serif;
    font-size: ${fontSizes[0]};
    color: ${colors.blue.primary};

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `}
`;

/**
 * Division picker for an Application Type, used by both the create and edit
 * screens — toggles styled like the record sharing panel
 * (`RecordAccess/AddShareForm`), laid out as a vertical list of clickable
 * rows. Application Types are scoped to Divisions only, not Teams. Settings
 * configures types for the whole tenant, so this lists every active Division
 * (not just the admin's own memberships). Must sit inside a Formik form whose
 * values include `divisions`.
 */
export const ApplicationTypeDivisionFields = ({
  isDisabled,
}: {
  isDisabled: boolean;
}) => {
  const { values, errors, touched, setFieldValue, setFieldTouched } =
    useFormikContext<DivisionValues>();

  const { data: divisionsQuery, isLoading } = useDivisions();
  const divisions = useMemo(
    () => (divisionsQuery?.data ?? []).filter((d) => d.is_active),
    [divisionsQuery?.data],
  );

  const selectedIds = values.divisions;
  const selectedCount = divisions.filter((d) =>
    selectedIds.includes(d.id),
  ).length;
  const allSelected =
    divisions.length > 0 && selectedCount === divisions.length;
  const hasError = Boolean(touched.divisions && errors.divisions);

  const setDivisions = (ids: number[]) => {
    setFieldValue("divisions", ids);
    setFieldTouched("divisions", true, false);
  };

  const toggleDivision = (id: number, checked: boolean) =>
    setDivisions(
      checked
        ? Array.from(new Set([...selectedIds, id]))
        : selectedIds.filter((existing) => existing !== id),
    );

  return (
    <Box my={3}>
      <Paragraph m={0} fontSize={1}>
        Divisions *
      </Paragraph>
      <Flex alignItems="center" gap="8" mb={1}>
        <Paragraph m={0} fontSize={0} color="gray.50">
          {divisions.length > 0
            ? `Available to ${selectedCount} of ${divisions.length} divisions`
            : "Choose which divisions can use this application type"}
        </Paragraph>
        {divisions.length > 0 && (
          <TextButton
            type="button"
            disabled={isDisabled}
            onClick={() =>
              setDivisions(allSelected ? [] : divisions.map(({ id }) => id))
            }
          >
            {allSelected ? "Clear all" : "Select all"}
          </TextButton>
        )}
      </Flex>

      {isLoading ? (
        <Paragraph m={0} py={1} fontSize={1} color="gray.50">
          Loading divisions...
        </Paragraph>
      ) : divisions.length === 0 ? (
        <Paragraph m={0} py={1} fontSize={1} color="gray.50">
          No active divisions. Add one in Divisions & Teams first.
        </Paragraph>
      ) : (
        <TileList>
          {divisions.map((division) => {
            const isSelected = selectedIds.includes(division.id);
            return (
              <Tile
                key={division.id}
                isSelected={isSelected}
                hasError={hasError}
                isDisabled={isDisabled}
                title={division.display_name}
                onClick={() =>
                  !isDisabled && toggleDivision(division.id, !isSelected)
                }
              >
                <Flex alignItems="center" gap="6" minWidth={0}>
                  <TileName
                    m={0}
                    fontSize={1}
                    color={isSelected ? "blue.100" : "gray.60"}
                    fontWeight={isSelected ? 500 : 400}
                  >
                    {division.display_name}
                  </TileName>
                  {division.is_default && <DefaultPill>Default</DefaultPill>}
                </Flex>
                {/* The tile's own onClick handles the toggle; stop the
                    toggle's click from bubbling up and flipping it twice. */}
                <Box onClick={(e) => e.stopPropagation()} flexShrink={0}>
                  <SmallToggle
                    isChecked={isSelected}
                    isDisabled={isDisabled}
                    onChange={(e) =>
                      toggleDivision(division.id, e.target.checked)
                    }
                  />
                </Box>
              </Tile>
            );
          })}
        </TileList>
      )}

      {hasError && (
        <Paragraph
          m={0}
          mt={1}
          maxWidth="480px"
          fontSize={1}
          color="red.primary"
          textAlign="right"
        >
          {errors.divisions as string}
        </Paragraph>
      )}
    </Box>
  );
};
